/**
 * @filmodasi/shared — contracts common to server and web.
 *
 * Types, frozen const maps, and the redaction patterns. Deliberately dependency-free
 * and free of runtime I/O so it can be imported from Node, the browser, and the Tizen
 * packaged app alike.
 *
 * All timestamps crossing the API are ISO-8601 UTC strings, e.g. "2026-08-07T12:00:00.000Z".
 */
/* ------------------------------------------------------------------ *
 * Library separation
 *
 * These two enums are the mechanism that keeps Live TV, Movies, and Series from
 * ever mixing. Every catalog query hard-filters on one of them.
 * ------------------------------------------------------------------ */
/** The three libraries. A Category belongs to exactly one. */
export const LIBRARY_KINDS = ['live', 'movie', 'series'];
/**
 * CatalogItem discriminator. `season` and `episode` are structural children of a
 * `series` and are never returned by browse endpoints — only under a series detail.
 */
export const ITEM_TYPES = ['live', 'movie', 'series', 'season', 'episode'];
/** The item types a browse endpoint may return, one per library. */
export const BROWSABLE_TYPES = ['live', 'movie', 'series'];
/** Maps a library to the single item type its browse endpoint may emit. */
export const LIBRARY_TO_ITEM_TYPE = Object.freeze({
    live: 'live',
    movie: 'movie',
    series: 'series',
});
export const SOURCE_TYPES = ['m3u', 'xtream'];
/* ------------------------------------------------------------------ *
 * Playback
 * ------------------------------------------------------------------ */
/**
 * Wire format actually served by the provider.
 *
 * `rtmp` is intentionally absent: providers advertise it in `allowed_output_formats`,
 * but no browser can play it.
 */
export const PLAYBACK_FORMATS = ['m3u8', 'ts', 'mp4', 'direct'];
/**
 * Client-side decoder.
 *
 * `hls` cannot play a bare MPEG-TS byte stream and `mpegts` cannot play an HLS
 * playlist, so this is chosen from the *resolved per-item* format, never from the
 * source default.
 */
/**
 * `remux` is the native element pointed at the server's audio-remuxing endpoint. It
 * exists because this provider's VOD library carries E-AC-3 audio, which no desktop
 * browser decodes — the video plays and there is simply no sound, and no volume control
 * either, since there is no decodable audio track for one to act on.
 */
export const PLAYER_ENGINES = ['hls', 'mpegts', 'native', 'avplay', 'remux'];
/**
 * Audio codecs to ask the browser about before playing.
 *
 * `canPlayType` is the only honest answer — Edge on Windows decodes E-AC-3 through the
 * platform while Chrome on the same machine does not, so a hardcoded table would be wrong
 * for half the household.
 */
export const AUDIO_CODEC_PROBES = Object.freeze({
    aac: 'audio/mp4; codecs="mp4a.40.2"',
    ac3: 'audio/mp4; codecs="ac-3"',
    eac3: 'audio/mp4; codecs="ec-3"',
    mp3: 'audio/mpeg',
    opus: 'audio/ogg; codecs="opus"',
    vorbis: 'audio/ogg; codecs="vorbis"',
    flac: 'audio/ogg; codecs="flac"',
    dts: 'audio/mp4; codecs="dtsc"',
});
/** Containers no browser decodes. Tizen AVPlay handles these, so it is device-dependent. */
export const BROWSER_UNPLAYABLE_CONTAINERS = ['mkv', 'avi', 'flv', 'wmv', 'mpg', 'mpeg'];
/** Default engine for a format on a normal browser. Tizen overrides to `avplay`. */
export const FORMAT_TO_WEB_ENGINE = Object.freeze({
    m3u8: 'hls',
    ts: 'mpegts',
    mp4: 'native',
    direct: 'native',
});
/** HLS playlist flavour, which decides the segment-map strategy server-side. */
export const PLAYLIST_KINDS = ['vod', 'live'];
export const REDACT_PATTERNS = Object.freeze([
    {
        name: 'streamToken',
        source: '(/api/stream/)[A-Za-z0-9_-]{16,}',
        flags: 'g',
        replacement: '$1<redacted>',
        why: 'The opaque stream token sits in the URL path, so any access log captures it verbatim.',
    },
    {
        name: 'xtreamCredentialPath',
        source: '/(live|movie|series)/[^/\\s?]+/[^/\\s?]+/',
        flags: 'gi',
        replacement: '/$1/<redacted>/<redacted>/',
        why: 'Xtream upstream URLs embed username and password as path segments.',
    },
    {
        name: 'secretQueryParam',
        source: '([?&](?:password|passwd|pwd|pass|token|access_token|refresh_token|auth|apikey|api_key|key|sig|signature)=)[^&\\s]+',
        flags: 'gi',
        replacement: '$1<redacted>',
        why: 'Credentials and signed tokens also travel as query parameters.',
    },
    {
        name: 'urlUserInfo',
        source: '([a-z][a-z0-9+.-]*://)[^/@\\s]+:[^/@\\s]+@',
        flags: 'gi',
        replacement: '$1<redacted>@',
        why: 'The user:pass@host form leaks credentials before the host.',
    },
]);
/** Applies every redaction pattern. Safe to call on arbitrary log text. */
export function redactText(input) {
    let out = input;
    for (const p of REDACT_PATTERNS) {
        out = out.replace(new RegExp(p.source, p.flags), p.replacement);
    }
    return out;
}
/* ------------------------------------------------------------------ *
 * Error codes — stable strings the client branches on.
 * ------------------------------------------------------------------ */
export const ERROR_CODES = [
    'VALIDATION_FAILED',
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'NOT_FOUND',
    'CONFLICT',
    'REGISTRATION_DISABLED',
    'DB_UNAVAILABLE',
    /** Session still present but past expiresAt — client should renew, not re-login. */
    'STREAM_SESSION_EXPIRED',
    /** Past hardExpiresAt; renewal refused, client must mint a fresh session. */
    'STREAM_SESSION_HARD_EXPIRED',
    'STREAM_LIMIT_REACHED',
    'UPSTREAM_UNAVAILABLE',
    'UNSUPPORTED_CONTAINER',
    'PROVIDER_AUTH_FAILED',
    'SOURCE_SYNC_IN_PROGRESS',
    /** Profile has a PIN and the request carried no valid profile grant. */
    'PROFILE_LOCKED',
    /** The PIN was wrong. Distinct from PROFILE_LOCKED so the UI can say which. */
    'PROFILE_PIN_INVALID',
    /**
     * `adultEnabled` was requested on a profile that is not capable of holding it.
     *
     * Its own code because the UI must not offer a retry: unlike every other refusal here,
     * there is no proof the user could supply that would change the answer. The capability
     * is granted only by an operator-run script against the database.
     */
    'PROFILE_NOT_ADULT_CAPABLE',
    'RATE_LIMITED',
    /**
     * The pairing code is unknown, expired, already used, or not yet approved.
     *
     * Deliberately one code for all four. Telling a caller which would turn the claim
     * endpoint into an oracle for guessing codes — "expired" means the guess was a real
     * code, and that is exactly the bit an attacker is missing.
     */
    'PAIRING_INVALID',
    /** The device is known but was revoked from the management screen. */
    'DEVICE_REVOKED',
    /** Body rejected by the parser before any route saw it — an oversized avatar, usually. */
    'PAYLOAD_TOO_LARGE',
    'INTERNAL',
];
/**
 * The supplied avatar illustrations.
 *
 * Ids rather than URLs, so the artwork can be redrawn or moved without touching stored
 * profiles, and so an avatar cannot become an arbitrary path the server or client fetches.
 * The files live at `/avatars/<id>.svg` in the web app; the server validates only that the
 * id is one of these.
 *
 * All original work — no third-party character artwork, which would be someone else's
 * property regardless of how private the deployment is.
 */
export const BUILT_IN_AVATARS = [
    'reel',
    'clapper',
    'ticket',
    'popcorn',
    'astronaut',
    'rocket',
    'wizard',
    'dragon',
    'robot',
    'cat',
    'fox',
    'owl',
    'panda',
    'dino',
    'ghost',
    'star',
];
/** Longest note that can travel with a recommendation. */
export const PROFILE_RECOMMENDATION_MESSAGE_MAX = 280;
/**
 * Avatar ceiling, in bytes of the encoded data URI.
 *
 * 256×256 WebP at reasonable quality lands around 12–20 KB, so 96 KB is generous while
 * keeping seven profiles well under Mongo's 16 MB document limit and, more to the point,
 * keeping the profile list response small enough to render instantly on a TV.
 */
export const PROFILE_AVATAR_MAX_BYTES = 96 * 1024;
/** Digits only. Short enough to enter on a TV remote, which is the whole point. */
export const PROFILE_PIN_LENGTH = 4;
/**
 * Client type, which decides how the refresh token is delivered.
 *
 * `web` receives it **only** as a Secure/httpOnly/SameSite cookie — never in the body
 * and never readable by frontend JavaScript. `tizen` is the dedicated packaged-app
 * flow, the only path permitted to return it in the response body, because a Tizen
 * widget runs from a local origin where cookies are unreliable.
 */
export const CLIENT_TYPES = ['web', 'tizen'];
/**
 * The age band an item was classified into.
 *
 * Deliberately *not* the same question as the hard adult gate, which stays server-side and
 * never reaches a client: a film rated 18 is hidden from a child, whereas that gate hides
 * pornography from every profile without the household's capability. Items behind the gate
 * are filtered out before a DTO is built, so this can be exposed without exposing it.
 *
 * `unknown` is common and is not an error — most of the catalogue has not been through
 * enrichment. A client that treats it as "no badge" is behaving correctly.
 */
export const MATURITY_LEVELS = ['kids', 'general', 'teen', 'adult', 'unknown'];
/* ------------------------------------------------------------------ *
 * Phase 2 — engagement layer
 * ------------------------------------------------------------------ */
/**
 * Percentage at which an item counts as finished.
 *
 * Not 100: films end with two minutes of credits nobody watches, so a viewer who has
 * seen the whole story stops somewhere in the low nineties. Requiring 100 would leave
 * every finished film sitting in Continue Watching forever, which is the single most
 * common complaint about players that get this wrong.
 */
export const PROGRESS_COMPLETE_PERCENT = 92;
/**
 * Floor for Continue Watching.
 *
 * Below this the viewer has seen a few seconds — usually a mis-click or a preview — and
 * a row full of things nobody actually started is worse than an empty one.
 */
export const PROGRESS_RESUME_MIN_PERCENT = 2;
/**
 * Homepage row identities.
 *
 * `because` and `similar` are parameterised at request time (`because:<itemId>`), so
 * this is the set of row *kinds*, not the set of cache keys.
 */
export const RECOMMENDATION_ROWS = [
    'continue',
    'recommended',
    'because',
    'similar-to-favorites',
    'newly-added-movie',
    'newly-added-series',
];
/* ------------------------------------------------------------------ *
 * Discovery — what replaces a wall of provider categories
 * ------------------------------------------------------------------ */
/**
 * The rows a library screen is built from, in order.
 *
 * Provider categories do not disappear; they move under "Tüm Kategoriler". What sits
 * above them is this table — the difference between browsing a *catalog* and browsing an
 * *IPTV subscription*.
 *
 * Declared here rather than in the server because the three clients need to reason about
 * a row's kind: the TV lays a `continue` row out differently (its cards carry progress),
 * and every client wants to know whether tapping a row header should open a filtered
 * grid. The *contents* are still built server-side, and so are the titles — the TV
 * carries no i18n runtime, which is an existing constraint, not a new one.
 */
export const DISCOVERY_ROW_KINDS = [
    'continue',
    'recommended',
    'because',
    'newly-added',
    'top-rated',
    'genre',
    'language',
];
/**
 * There is no "Popular" row, and that is deliberate.
 *
 * The provider sends no popularity signal, and one household generates no meaningful
 * ranking of its own — a "Popular" row here could only ever be the provider's static
 * rating field wearing a different hat. Calling that Popular would be a small lie the
 * interface tells every single day, so the row says what it actually is: highly rated,
 * within a recency window.
 */
export const TOP_RATED_MIN = 7;
/* ------------------------------------------------------------------ *
 * Devices and pairing
 * ------------------------------------------------------------------ */
export const DEVICE_TYPES = ['tv', 'phone', 'tablet', 'desktop'];
/**
 * Six characters from an alphabet with no `0/O` and no `1/I/L`.
 *
 * The code is read off a television across a room and typed on a phone, so the pairs that
 * look alike in a TV's font are the ones that produce a failed pairing and a second
 * attempt. Dropping them costs about half a bit of entropy each, which the short expiry
 * and the rate limit already account for.
 */
export const PAIRING_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const PAIRING_CODE_LENGTH = 6;
/** Long enough to walk to the phone, short enough that a shoulder-surfed code is stale. */
export const PAIRING_TTL_MINUTES = 5;
/**
 * Bounds, because this crosses a socket.
 *
 * Nothing here is attacker-controlled in the usual sense — the payload comes from the
 * household's own television — but a malformed provider stream can produce a track list of
 * surprising size or a label of surprising length, and the phone would render whatever
 * arrived. The limits are generous against real files and firm against nonsense.
 */
export const REMOTE_TRACKS_MAX = 24;
export const REMOTE_TRACK_ID_MAX = 64;
export const REMOTE_TRACK_LABEL_MAX = 80;
/* ------------------------------------------------------------------ *
 * Small shared helpers
 * ------------------------------------------------------------------ */
/** Normalises a title for search and similarity: lowercase, de-accented, de-prefixed. */
export function normalizeTitle(raw) {
    return raw
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .replace(/^\s*[a-z]{2,3}\s*[:|-]\s*/i, '') // strip "TR: ", "EN | "
        .replace(/\b(?:4k|uhd|fhd|hd|sd|hevc|h265|h264|multi|vip)\b/gi, ' ')
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/** Token set used for Jaccard similarity in the Phase 2 recommender. */
export function titleTokens(raw) {
    const stop = new Set(['the', 'a', 'an', 'of', 'and', 've', 'ile', 'bir']);
    return Array.from(new Set(normalizeTitle(raw)
        .split(' ')
        .filter((t) => t.length > 1 && !stop.has(t))));
}
//# sourceMappingURL=index.js.map